import { FC, useEffect } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { Header, Layout, NextButton } from '@eneco/flows/components';
import { useFlowHooks } from '@eneco/flows/utils/FlowProvider';
import { useFormatter } from '@eneco/i18n';
import { InputDatepicker, NotificationBox, Stack } from '@eneco/sparky';
import { usePlaceholderContent } from '@sitecore/common';
import { DeceasedFlowKeyHandoverDateStepRendering } from '@sitecore/types/DeceasedFlowKeyHandoverDateStep';
import { Controller, useForm } from 'react-hook-form';

import { FlowContextDeceased } from '../../context';

interface FormFields {
  keyDateNewAddress: string;
}

export const KeyHandOver: FC = () => {
  const { useFlowActorRef, useFlowSelector } = useFlowHooks<FlowContextDeceased>();
  const { send: sendToFlowMachine } = useFlowActorRef();
  const keyDateNewAddress = useFlowSelector(state => state.context.keyDateNewAddress);
  const { DeceasedFlowKeyHandoverDateStep: { fields: textualData } = {} } = usePlaceholderContent<{
    DeceasedFlowKeyHandoverDateStep: DeceasedFlowKeyHandoverDateStepRendering;
  }>();

  const { date } = useFormatter();

  const {
    handleSubmit,
    getValues,
    control,
    formState: { errors, isSubmitSuccessful },
  } = useForm<FormFields>({
    mode: 'onSubmit',
    defaultValues: {
      keyDateNewAddress,
    },
  });

  useEffect(() => {
    if (isSubmitSuccessful) {
      const formField = getValues();
      sendToFlowMachine({
        type: 'NEXT_PAGE',
        values: {
          ...formField,
        },
      });
    }
  }, [getValues, isSubmitSuccessful, sendToFlowMachine]);

  return (
    <Layout.Content variant="B" handleSubmit={handleSubmit}>
      <Header>
        <Header.Title>{textualData?.content?.title?.value}</Header.Title>
        {textualData?.content?.text?.value && <Header.Text>{textualData.content.text.value}</Header.Text>}
      </Header>
      <Stack.Item>
        <Controller
          control={control}
          name="keyDateNewAddress"
          render={({ field: { onChange, value, name } }) => (
            <InputDatepicker
              label={textualData?.handoverDate?.handoverDateFormField?.value?.label ?? ''}
              hint={textualData?.handoverDate?.handoverDateFormField?.value?.hint ?? ''}
              placeholder={textualData?.handoverDate?.handoverDateFormField?.value?.placeholder ?? ''}
              error={errors.keyDateNewAddress?.message}
              value={value}
              name={name}
              fromDate={new Date(new Date().setDate(new Date().getDate() + 1))}
              onDaySelect={d => onChange(d && date.short(d as Date))}
            />
          )}
        />
      </Stack.Item>
      <Stack.Item>
        <NotificationBox
          isAlert={false}
          variant="info"
          text={<RichText html={textualData?.handoverDate?.handoverDateUnknownNotification?.value?.content ?? ''} />}
          title={textualData?.handoverDate?.handoverDateUnknownNotification?.value?.title ?? ''}
        />
      </Stack.Item>
      <Stack.Item>
        <NextButton isLoading={isSubmitSuccessful} alignX="start">
          {textualData?.content.nextStepText?.value}
        </NextButton>
      </Stack.Item>
    </Layout.Content>
  );
};
